import { Component, OnInit } from '@angular/core'
import { PayoutBatchService } from '../../../services/payout-batch.service'
import { ActivatedRoute } from '@angular/router'
import { PayoutService } from 'src/app/services/payout.service'
import { FirestorePaths, InvoiceStatus, Merchant, Payout, PayoutBatch, PayoutBatchStatusEnum, PayoutFrequencyEnum, PayoutStatusEnum, EnumNames } from '@parkupp/core'
import { MerchantService } from 'src/app/services/merchant.service'
import { serverTimestamp } from '@angular/fire/firestore'
import { doc, writeBatch } from '@angular/fire/firestore'
import { InvoiceService } from 'src/app/services/invoice.service'
import * as firestore from 'firebase/firestore'

@Component({
    selector: 'app-admin-payout-batch-details',
    templateUrl: './admin-payout-batch-details.component.html',
    styleUrls: ['./admin-payout-batch-details.component.scss'],
})
export class AdminPayoutBatchDetailsComponent implements OnInit {
    EnumNames = EnumNames
    PayoutFrequencyEnum = PayoutFrequencyEnum
    PayoutStatusEnum = PayoutStatusEnum
    PayoutBatchStatusEnum = PayoutBatchStatusEnum
    payoutBatch: PayoutBatch
    payouts: Payout[] = []

    constructor(
        private payoutBatchService: PayoutBatchService,
        private route: ActivatedRoute,
        private payoutService: PayoutService,
        private merchantService: MerchantService,
        private invoiceService: InvoiceService
    ) {}

    async ngOnInit() {
        this.payoutBatch = await this.payoutBatchService.getPayoutBatch(this.route.snapshot.params['payoutBatchKey'])

        for (const payoutRef of this.payoutBatch.payoutRefs) {
            const payout = await this.payoutService.getPayout(payoutRef.id)
            if (payout.merchantRef) {
                payout.$merchant = await this.merchantService.getMerchant(payout.merchantRef.id)
            }
            this.payouts.push(payout)
        }
    }
    async completePayoutBatch() {
        const updatePayoutsBatch = writeBatch(this.payoutService.firestore)
        for (const payoutRef of this.payoutBatch.payoutRefs) {
            const payoutDocRef = this.payoutService.getDocRef(payoutRef.id)
            updatePayoutsBatch.update(payoutDocRef, { status: PayoutStatusEnum.PAID, payoutDate: serverTimestamp() })
        }
        await updatePayoutsBatch.commit()

        const invoiceBatch = writeBatch(this.invoiceService.firestore)
        for (const invoiceRef of this.payoutBatch.invoiceRefs) {
            const invoiceDocRef = this.invoiceService.getDocRef(invoiceRef.id)
            invoiceBatch.update(invoiceDocRef, { paidOut: true })
        }
        await invoiceBatch.commit()

        this.payoutBatch.status = PayoutBatchStatusEnum.PAID
        this.payoutBatch.payoutDate = serverTimestamp() as firestore.Timestamp
        await this.payoutBatchService.createOrUpdate(this.payoutBatch)
        location.reload()
    }
    async deletePayoutBatch() {
        if (confirm('Are you sure you want to delete this payout batch?')) {
            if (this.payoutBatch.$key) {
                await this.payoutBatchService.deletePayoutBatch(this.payoutBatch.$key)
                location.reload()
            }
        }
    }

    async exportStandardBankCSV() {
        // Format payouts data for Standard Bank CSV export
        const csvData = []

        // Header row with Standard Bank required fields
        const header = ['Beneficiary Name', 'Account Number', 'Account Type', 'Bank', 'Branch Code', 'Reference', 'Amount', 'Your Reference']
        csvData.push(header.join(','))

        // Add a row for each payout
        for (const payout of this.payouts) {
            if (!payout.$merchant?.payoutDetails) {
                console.warn(`Missing payment details for merchant: ${payout.$merchant?.companyName}`)
                continue
            }

            // Get bank branch code based on bank type
            const branchCode = this.getBankBranchCode(payout.$merchant.payoutDetails.bank)

            // Get account type code (needed for Standard Bank format)
            const accountTypeCode = this.getAccountTypeCode(payout.$merchant.payoutDetails.accountType)

            // Format data for Standard Bank CSV format
            const merchantName = payout.$merchant.payoutDetails.beneficiaryName || payout.$merchant.payoutDetails.accountHolderName
            const accountNumber = payout.$merchant.payoutDetails.accountNumber
            const reference = `PFI-${payout.payoutNumber}` // Use the proforma invoice number as reference
            const amount = payout.payoutAmount.toFixed(2) // Format to 2 decimal places
            const yourReference = `PUP-${payout.$merchant.companyName.substring(0, 10)}` // ParkUpp reference for tracking

            // Create CSV row
            const row = [merchantName, accountNumber, accountTypeCode, payout.$merchant.payoutDetails.bank, branchCode, reference, amount, yourReference]

            // Add row to CSV data, escaping any commas in text fields
            csvData.push(row.map((field) => this.escapeCsvField(field)).join(','))
        }

        // Create and download the CSV file
        const csvContent = csvData.join('\n')
        const date = new Date().toLocaleDateString('en-CA')
        const filename = `${date}-ParkUpp-Standard-Bank-Batch-${this.payoutBatch.payoutBatchNumber}.csv`

        this.downloadCsv(csvContent, filename)
    }

    // Helper method to get bank branch code
    private getBankBranchCode(bank: string): string {
        const branchCodes: Record<string, string> = {
            ABSA: '632005',
            BIDVEST: '462005',
            CAPITEC: '470010',
            DISCOVERY: '679000',
            FNB: '250655',
            INVESTEC: '580105',
            NEDBANK: '198765',
            STANDARD: '051001',
            TYME: '678910',
        }

        return branchCodes[bank] || '000000'
    }

    // Helper method to convert account type to Standard Bank format
    private getAccountTypeCode(accountType: string): string {
        const accountTypeCodes: Record<string, string> = {
            CURRENT: '1',
            SAVINGS: '2',
            TRANSMISSION: '3',
            CREDIT_CARD: '4',
        }

        return accountTypeCodes[accountType] || '1'
    }

    // Escape CSV fields containing commas, quotes, etc.
    private escapeCsvField(field: any): string {
        if (field === null || field === undefined) {
            return ''
        }

        const stringField = String(field)

        // If the field contains commas, quotes, or newlines, enclose it in quotes
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
            // Replace any quotes with double quotes to escape them
            return `"${stringField.replace(/"/g, '""')}"`
        }

        return stringField
    }

    // Download CSV file
    private downloadCsv(csvContent: string, filename: string): void {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')

        // Create a URL for the Blob
        const url = URL.createObjectURL(blob)

        // Set link properties
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'

        // Add link to the document, click it, and remove it
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }
}
